"use client"

import { motion, useScroll, useTransform, useMotionValue, useSpring, type Variants } from "framer-motion"
import { ArrowDown } from "lucide-react"
import Image from "next/image"
import { useRef, type MouseEvent, type ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * Motion vocabulary for FullBleedImageHero. Cinematic > polite.
 *
 *   - Background image: continuous slow Ken Burns zoom (ambient) + scroll-
 *     linked parallax (image moves slower than scroll). Together this gives
 *     the "alive cinema" feel — the image breathes when the page is still
 *     AND responds to scroll when the user moves.
 *   - Overlay text: viewport-entry reveal with cinematic delay (eyebrow
 *     first, then headline, then subhead, then CTAs). The user sees the
 *     story unfold instead of arriving as a flat block.
 *   - Scroll cue: subtle animated chevron at the bottom of the viewport,
 *     fades out as you scroll past 25%. Tells visitors "there's more."
 *   - Magnetic CTAs: pull toward cursor with spring physics.
 *
 * Server component (FullBleedImageHero) owns SEO-critical headline/copy;
 * this island owns the cinematic motion layer.
 */

interface FullBleedImageBackgroundProps {
  src: string
  alt: string
  focalPoint?: string
}

export function FullBleedImageBackground({ src, alt, focalPoint = "center" }: FullBleedImageBackgroundProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] })

  // Scroll-linked parallax: image drifts down slower than scroll, so it
  // feels "stuck" to the section as you scroll past. Subtle scale change
  // keeps proportions natural (no awkward letterbox at the edges).
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"])
  const scale = useTransform(scrollYProgress, [0, 1], [1.0, 1.12])

  return (
    <div ref={ref} aria-hidden="true" className="absolute inset-0 -z-20 overflow-hidden">
      {/* Continuous Ken Burns ambient zoom — runs even when scroll is still */}
      <motion.div
        className="absolute inset-0"
        style={{ y, scale }}
        animate={{ scale: [1.0, 1.06, 1.0] }}
        transition={{ duration: 22, ease: "easeInOut", repeat: Infinity }}
      >
        <Image
          src={src}
          alt={alt}
          fill
          sizes="100vw"
          priority
          className="object-cover"
          style={{ objectPosition: focalPoint }}
        />
      </motion.div>
      {/* Cinematic vignette + readability gradient */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,hsl(var(--foreground)/0.42)_0%,hsl(var(--foreground)/0.18)_28%,hsl(var(--foreground)/0.18)_52%,hsl(var(--foreground)/0.82)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_55%,transparent_30%,hsl(var(--foreground)/0.38)_100%)]" />
    </div>
  )
}

/**
 * Cinematic text overlay — eyebrow → headline → subhead → CTAs stagger in
 * on viewport entry with deliberate, slow timing. The point is theatrical:
 * the user *sees* the story arrive.
 */
export function FullBleedImageOverlay({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-20%" }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.18, delayChildren: 0.2 } },
      }}
      className="relative z-10 flex h-full flex-col justify-end pb-24 pt-20 sm:pb-32 lg:pb-40"
    >
      {children}
    </motion.div>
  )
}

const REVEAL: Variants = {
  hidden: { opacity: 0, y: 24, filter: "blur(8px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] } },
}

export function FullBleedReveal({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div variants={REVEAL} className={className}>
      {children}
    </motion.div>
  )
}

/**
 * Scroll-down cue. Subtle animated chevron in the bottom-center of the
 * viewport — gently bounces, fades as the user scrolls past 25% of section.
 */
export function FullBleedScrollCue() {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] })
  const opacity = useTransform(scrollYProgress, [0, 0.25], [1, 0])

  return (
    <motion.div
      ref={ref}
      style={{ opacity }}
      className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 text-background/85"
    >
      <span className="text-[10px] font-medium uppercase tracking-[0.32em]">Scroll</span>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, ease: "easeInOut", repeat: Infinity }}
      >
        <ArrowDown className="size-5" />
      </motion.div>
    </motion.div>
  )
}

/**
 * Magnetic CTA wrapper — pulls toward cursor with spring physics.
 * Tasteful (max 14px translation), springy snap-back on leave.
 */
export function FullBleedMagneticCta({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const sx = useSpring(x, { stiffness: 200, damping: 18, mass: 0.3 })
  const sy = useSpring(y, { stiffness: 200, damping: 18, mass: 0.3 })

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 16)
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * 12)
  }
  function handleMouseLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: sx, y: sy }}
      className={cn("inline-flex", className)}
    >
      {children}
    </motion.div>
  )
}
